import * as z from 'zod';
import { Tfn } from '@jd/i18n/types';
import {
  formFiles,
  getFileExtension,
  normalizeFileExtension,
} from '@jd/platform-config/constants';
import { FormField } from '../../type';
import { FileField, isFileField } from './types';
import { toFiles } from './toFiles';

const MB = 1024 * 1024;

/** Per field: required, single vs. many files and the allowed extensions. */
export const fileFieldValidator = (field: FileField, common_t: Tfn<'common'>) =>
  z.any().superRefine((value, ctx) => {
    const files = toFiles(value);
    const allowed = (field.allowedExtensions ?? []).map(normalizeFileExtension);

    if (field.required && !files.length) {
      ctx.addIssue({ code: 'custom', message: common_t('isRequired') });
    } else if (!field.multiple && files.length > 1) {
      ctx.addIssue({ code: 'custom', message: common_t('singleFileOnly') });
    } else if (
      allowed.length &&
      files.some((file) => !allowed.includes(getFileExtension(file.name)))
    ) {
      ctx.addIssue({
        code: 'custom',
        message: common_t('fileTypeNotAllowed', {
          extensions: allowed.join(', '),
        }),
      });
    }
  });

/** Across all file fields: the per-submission count and size limits. */
export const refineFileLimits =
  (fields: FormField[], common_t: Tfn<'common'>) =>
  (values: Record<string, unknown>, ctx: z.RefinementCtx) => {
    const fileFields = fields.filter(isFileField);
    const lastField = fileFields[fileFields.length - 1];
    if (!lastField) return;

    const files = fileFields.flatMap((field) => toFiles(values[field.name]));
    const totalBytes = files.reduce((sum, file) => sum + file.size, 0);

    if (files.length > formFiles.maxFiles) {
      ctx.addIssue({
        code: 'custom',
        path: [lastField.name],
        message: common_t('tooManyFiles', { max: formFiles.maxFiles }),
      });
    } else if (totalBytes > formFiles.maxTotalBytes) {
      ctx.addIssue({
        code: 'custom',
        path: [lastField.name],
        message: common_t('filesTooLarge', {
          max: formFiles.maxTotalBytes / MB,
        }),
      });
    }
  };
